import './offers-view.scss';
import { DialogService } from 'aurelia-dialog';
import { autoinject } from 'aurelia-framework';
import { MooringCreateOfferDialog } from 'features/mooring/offers/mooring-create-offer-dialog';
import { AsyncErrorHandler, ViewLoadingFader } from 'lib/ui';
import { MooringOfferListItemDto, MooringOfferStatusTranslation } from 'models/mooring';
import { MooringOfferService } from 'services/mooring';
import { PubSub } from 'lib/event/PubSub';
import { FilterManager } from 'lib/tables/FilterManager';
import { OfferView } from './offer-view';
import { Filters } from 'elements/Filter';
import { I18N } from 'aurelia-i18n';
import { CustomerService } from 'services/customer-service';
import { UserService } from 'services/user-service';
import { ContactService } from 'services/contact-service';
import { SiteService } from 'services/site-service';

@autoinject
export class OffersView {
  protected offers: MooringOfferListItemDto[] = [];

  constructor(
    protected element: Element,
    protected fm: FilterManager,
    private moooringOfferService: MooringOfferService,
    private dialogService: DialogService,
    private customerService: CustomerService,
    private contactService: ContactService,
    private siteService: SiteService,
    private userService: UserService,
    private t: I18N,
    private pubsub: PubSub
  ) {
    pubsub.sub('filter-manager:filter-changed', () => {
      void this.getAllOffers();
    });
  }

  @AsyncErrorHandler
  async getCustomers() {
    const customres = await this.customerService.getAll();
    return customres.filter((x) => !x.IsDeleted);
  }

  @AsyncErrorHandler
  async getContacts() {
    const contacts = await this.contactService.getAll();
    return contacts.filter((x) => !x.IsDeleted);
  }

  @AsyncErrorHandler
  async getSites() {
    const sites = await this.siteService.getAll();
    return sites.filter((x) => !x.IsDeleted);
  }

  @AsyncErrorHandler
  async getSalesPersons() {
    const salesPerson = await this.userService.getMooringSellers();
    return salesPerson.data;
  }

  @ViewLoadingFader
  async attached() {
    const customers = await this.getCustomers();
    const contacts = await this.getContacts();
    const sites = await this.getSites();
    const salesPersons = await this.getSalesPersons();
    this.fm.init({
      context: OfferView.name,
      storageKey: 'mooring-offers',
      filters: {
        customerIds: {
          type: 'checkbox',
          label: this.t.tr('general.customer'),
          name: 'customerIds',
          query: 'customerIds',
          originalData: customers,
          options: customers.map((x) => ({
            Name: x.Name,
            Id: x.Id,
          })),
          disabled: false,
        },
        contactIds: {
          type: 'checkbox',
          label: this.t.tr('general.contact'),
          name: 'contactIds',
          query: 'contactIds',
          originalData: contacts,
          options: contacts.map((x) => ({
            Name: x.Name,
            Id: x.Id,
          })),
          disabled: false,
        },
        siteIds: {
          type: 'checkbox',
          label: this.t.tr('general.locality'),
          name: 'siteIds',
          query: 'siteIds',
          originalData: sites,
          options: sites.map((x) => ({
            Name: x.Name,
            Id: x.Id,
          })),
          disabled: false,
        },
        salesPersonIds: {
          type: 'checkbox',
          label: this.t.tr('general.seller'),
          name: 'salesPersonIds',
          query: 'salesPersonIds',
          originalData: salesPersons,
          options: salesPersons.map((x) => ({
            Name: x.Name,
            Id: x.Id,
          })),
          disabled: false,
        },
        statusIds: {
          type: 'checkbox',
          label: 'Status',
          name: 'statusIds',
          query: 'statusIds',
          originalData: MooringOfferStatusTranslation,
          options: Object.entries(MooringOfferStatusTranslation).map(([id, translation]) => ({
            Name: this.t.tr<string>(translation),
            Id: id,
          })),
          disabled: false,
        },
      } satisfies Filters,
      defaultFilters: {
        customerIds: true,
        contactIds: true,
        siteIds: true,
        salesPersonIds: true,
        statusIds: true,
      },
    });

    await this.getAllOffers();
  }

  @AsyncErrorHandler
  async getAllOffers() {
    const { data, headers } = await this.moooringOfferService.getOffers(this.fm.queryFilter);
    const record = headers.get('x-total-count');
    this.fm.paginationTotalRecords = record ? +record : 0;
    this.offers = data;
  }

  protected openCreateOfferDialog() {
    void this.dialogService.open({
      viewModel: MooringCreateOfferDialog,
    });
  }

  protected detached() {
    this.pubsub.unsub();
  }
}
